// Standalone Keycloak OIDC authentication for embedded widgets.
//
// Uses Authorization Code + PKCE with a browser popup (window.open)
// and postMessage to hand the auth code back to the widget.
//
// This is intentionally self-contained — no oidc-spa, no host-side
// oidcEarlyInit required. The widget can be dropped into any page
// and handle its own login/logout without touching the host app.

import type { OidcAuthConfig } from '../types'
import { setOidcTokenProvider } from '../ofetch-client'

export interface WidgetAuthState {
  isAuthenticated: boolean
  isLoading: boolean
  userEmail?: string
  userName?: string
}

type AuthStateListener = (state: WidgetAuthState) => void

interface StoredTokens {
  accessToken: string
  refreshToken: string | null
  expiresAt: number
  userEmail?: string
  userName?: string
}

// ── Module state ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'reqdesk-widget-auth'
const EXPIRY_MARGIN_S = 30
const POPUP_FEATURES = 'width=520,height=680,menubar=no,toolbar=no,location=no,status=no'
const POPUP_NAME = 'reqdesk-widget-login'

let config: OidcAuthConfig | null = null
let listeners: AuthStateListener[] = []
let initPromise: Promise<void> | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null

// Hydrate tokens + auth state synchronously from localStorage at module load,
// so the first React render already sees the correct authenticated status —
// no flicker, no race with the parallel useEffect that calls initWidgetAuth().
let tokens: StoredTokens | null = readStoredSync()
let authState: WidgetAuthState = tokens && Date.now() < tokens.expiresAt - EXPIRY_MARGIN_S * 1000
  ? {
      isAuthenticated: true,
      isLoading: false,
      userEmail: tokens.userEmail,
      userName: tokens.userName,
    }
  : { isAuthenticated: false, isLoading: false }

function readStoredSync(): StoredTokens | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredTokens
    if (!parsed.accessToken || !parsed.expiresAt) return null
    return parsed
  } catch {
    return null
  }
}

// ── Public state helpers ──────────────────────────────────────────────────────

function notify() {
  for (const listener of listeners) {
    try { listener(authState) } catch { /* ignore */ }
  }
}

function setState(update: Partial<WidgetAuthState>) {
  authState = { ...authState, ...update }
  notify()
}

export function getAuthState(): WidgetAuthState {
  return authState
}

export function onAuthStateChange(listener: AuthStateListener): () => void {
  listeners.push(listener)
  return () => {
    listeners = listeners.filter(l => l !== listener)
  }
}

export function isAuthConfigured(): boolean {
  return config !== null
}

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function randomBase64Url(bytes = 32): string {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return base64UrlEncode(arr.buffer)
}

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return base64UrlEncode(hash)
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1] + '==='.slice((parts[1].length + 3) % 4)
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

// ── Token storage (localStorage — persists across tabs and refreshes) ─────────

function saveStored(t: StoredTokens | null) {
  try {
    if (t) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t))
      console.debug('[reqdesk-widget] tokens saved to localStorage', { expiresAt: new Date(t.expiresAt).toISOString() })
    } else {
      localStorage.removeItem(STORAGE_KEY)
      console.debug('[reqdesk-widget] tokens removed from localStorage')
    }
  } catch (err) { console.warn('[reqdesk-widget] localStorage write failed:', err) }
}

// ── Keycloak URLs ─────────────────────────────────────────────────────────────

function authEndpoint(cfg: OidcAuthConfig) { return `${cfg.issuerUri}/protocol/openid-connect/auth` }
function tokenEndpoint(cfg: OidcAuthConfig) { return `${cfg.issuerUri}/protocol/openid-connect/token` }
function logoutEndpoint(cfg: OidcAuthConfig) { return `${cfg.issuerUri}/protocol/openid-connect/logout` }

// The callback page the popup redirects to. We redirect back to the host's
// current URL — Keycloak appends ?code=&state= as query params. We never
// actually let that URL replace the host page: we read the popup's
// window.location from the parent (same-origin) and close the popup.
function redirectUri(): string {
  // Use a same-origin blank path that's very unlikely to be a real route.
  // The popup WILL navigate here, but we close it before anyone sees it.
  return `${window.location.origin}/reqdesk-widget-auth-callback`
}

// ── Init / refresh lifecycle ─────────────────────────────────────────────────

function scheduleRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer)
  if (!tokens?.refreshToken) return

  const msUntilRefresh = Math.max(0, tokens.expiresAt - Date.now() - EXPIRY_MARGIN_S * 1000)
  refreshTimer = setTimeout(() => { void refresh() }, msUntilRefresh)
}

function applyTokens(t: StoredTokens) {
  tokens = t
  saveStored(t)
  setState({
    isAuthenticated: true,
    isLoading: false,
    userEmail: t.userEmail,
    userName: t.userName,
  })
  scheduleRefresh()
}

function clearTokens() {
  tokens = null
  saveStored(null)
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null }
  setState({ isAuthenticated: false, isLoading: false, userEmail: undefined, userName: undefined })
}

function parseTokenResponse(data: Record<string, unknown>): StoredTokens {
  const accessToken = String(data.access_token)
  const refreshToken = data.refresh_token ? String(data.refresh_token) : null
  const expiresIn = Number(data.expires_in ?? 300)
  const expiresAt = Date.now() + expiresIn * 1000

  const payload = decodeJwtPayload(accessToken) ?? {}
  return {
    accessToken,
    refreshToken,
    expiresAt,
    userEmail: typeof payload.email === 'string' ? payload.email : undefined,
    userName:
      typeof payload.name === 'string' ? payload.name :
      typeof payload.preferred_username === 'string' ? payload.preferred_username : undefined,
  }
}

async function refresh(): Promise<void> {
  if (!config || !tokens?.refreshToken) { clearTokens(); return }

  try {
    const res = await fetch(tokenEndpoint(config), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId,
        refresh_token: tokens.refreshToken,
      }),
    })
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      throw new Error(`refresh failed: ${res.status} ${body}`)
    }
    const data = await res.json()
    applyTokens(parseTokenResponse(data))
  } catch (err) {
    console.warn('[reqdesk-widget] token refresh failed:', err)
    clearTokens()
  }
}

async function getAccessToken(): Promise<string | null> {
  if (!tokens) return null
  // Still valid?
  if (Date.now() < tokens.expiresAt - EXPIRY_MARGIN_S * 1000) {
    return tokens.accessToken
  }
  // Try refresh
  if (tokens.refreshToken) {
    await refresh()
    return tokens?.accessToken ?? null
  }
  clearTokens()
  return null
}

// ── Init — restore tokens from localStorage and set up token provider ──────

export function initWidgetAuth(authConfig: OidcAuthConfig): Promise<void> {
  config = authConfig
  initPromise = _initWidgetAuth()
  return initPromise
}

async function _initWidgetAuth(): Promise<void> {
  setState({ isLoading: true })

  // Wire the ofetch interceptor to always fetch a fresh token from us
  setOidcTokenProvider(async () => {
    const token = await getAccessToken()
    return { accessToken: token ?? '' }
  })

  const stored = readStoredSync()
  console.debug('[reqdesk-widget] init: stored tokens?', stored ? 'yes' : 'no', stored ? { expiresAt: new Date(stored.expiresAt).toISOString(), hasRefresh: !!stored.refreshToken } : null)

  if (stored) {
    tokens = stored
    // If expired, try refresh; otherwise just mark logged in.
    if (Date.now() >= stored.expiresAt - EXPIRY_MARGIN_S * 1000) {
      console.debug('[reqdesk-widget] init: tokens expired, attempting refresh')
      await refresh()
    } else {
      console.debug('[reqdesk-widget] init: tokens valid, using as-is')
      setState({
        isAuthenticated: true,
        isLoading: false,
        userEmail: stored.userEmail,
        userName: stored.userName,
      })
      scheduleRefresh()
    }
  } else {
    setState({ isLoading: false })
  }
}

// ── Login — open Keycloak in a popup, receive code via postMessage ───────────

export async function login(): Promise<void> {
  if (!config) {
    console.warn('[reqdesk-widget] login() called before initWidgetAuth()')
    return
  }

  const cfg = config
  const state = randomBase64Url(16)
  const codeVerifier = randomBase64Url(32)
  const codeChallenge = await sha256(codeVerifier)

  const authUrl = new URL(authEndpoint(cfg))
  authUrl.searchParams.set('response_type', 'code')
  authUrl.searchParams.set('client_id', cfg.clientId)
  authUrl.searchParams.set('redirect_uri', redirectUri())
  authUrl.searchParams.set('scope', 'openid email profile')
  authUrl.searchParams.set('state', state)
  authUrl.searchParams.set('code_challenge', codeChallenge)
  authUrl.searchParams.set('code_challenge_method', 'S256')

  const popup = window.open(authUrl.toString(), POPUP_NAME, POPUP_FEATURES)
  if (!popup) {
    console.warn('[reqdesk-widget] login popup was blocked by the browser')
    return
  }

  // Poll the popup for the callback redirect (same-origin, so we can read .location.search).
  // Keycloak redirects to redirectUri() with ?code=...&state=...
  const code = await new Promise<string | null>(resolve => {
    let settled = false

    const finish = (result: string | null, err: string | null) => {
      if (settled) return
      settled = true
      clearInterval(poll)
      try { if (!popup.closed) popup.close() } catch { /* ignore */ }
      if (err) console.warn('[reqdesk-widget] login failed:', err)
      resolve(result)
    }

    const poll = setInterval(() => {
      // Popup manually closed by the user
      if (popup.closed) { finish(null, 'popup closed'); return }

      let href: string | null = null
      try {
        // Same-origin access — only possible once Keycloak redirects back to our origin
        href = popup.location.href
      } catch {
        // Cross-origin while on Keycloak's domain — ignore and keep polling
        return
      }

      if (!href || !href.startsWith(redirectUri())) return

      const url = new URL(href)
      const returnedState = url.searchParams.get('state')
      const returnedCode = url.searchParams.get('code')
      const error = url.searchParams.get('error')

      if (error) { finish(null, error); return }
      if (returnedState !== state) { finish(null, 'state mismatch'); return }
      finish(returnedCode, null)
    }, 400)
  })

  if (!code) return

  // Exchange code → tokens
  setState({ isLoading: true })
  try {
    const res = await fetch(tokenEndpoint(cfg), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: cfg.clientId,
        code,
        redirect_uri: redirectUri(),
        code_verifier: codeVerifier,
      }),
    })
    if (!res.ok) throw new Error(`token exchange failed: ${res.status}`)
    const data = await res.json()
    applyTokens(parseTokenResponse(data))
  } catch (err) {
    console.warn('[reqdesk-widget] token exchange failed:', err)
    setState({ isLoading: false })
  }
}

// ── Logout — revoke refresh token best-effort, clear local state ─────────────

export async function logout(): Promise<void> {
  if (!config) { clearTokens(); return }

  const cfg = config
  const refreshToken = tokens?.refreshToken

  clearTokens()

  if (refreshToken) {
    try {
      await fetch(logoutEndpoint(cfg), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: cfg.clientId,
          refresh_token: refreshToken,
        }),
      })
    } catch { /* non-critical */ }
  }
}
